import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CloseIcon from '@mui/icons-material/Close';
import ReactMarkdown from 'react-markdown';

// Helper functions
const adjustPercentages = (positive, neutral, negative, total, hideNeutral = true) => {
    if (total === 0) return { display: { pos: 0, neut: 0, neg: 0 }, adjusted: { pos: 0, neut: 0, neg: 0 } };
    
    // If hideNeutral is true, redistribute neutral votes proportionally
    let adjustedPositive = positive;
    let adjustedNegative = negative;
    let adjustedTotal = total;
    
    if (hideNeutral && neutral > 0) {
        const ratio = total > neutral ? (total - neutral) / total : 0;
        if (ratio > 0) {
            adjustedPositive = positive * (1 / ratio);
            adjustedNegative = negative * (1 / ratio);
            adjustedTotal = adjustedPositive + adjustedNegative;
        }
    }
    
    const rawPercentages = {
        pos: Math.round((adjustedPositive / adjustedTotal) * 100),
        neut: hideNeutral ? 0 : Math.round((neutral / total) * 100),
        neg: Math.round((adjustedNegative / adjustedTotal) * 100)
    };
    
    const exactPercentages = {
        pos: (adjustedPositive / adjustedTotal) * 100,
        neut: hideNeutral ? 0 : (neutral / total) * 100,
        neg: (adjustedNegative / adjustedTotal) * 100
    };
    
    const roundingDiffs = {
        pos: Math.abs(exactPercentages.pos - rawPercentages.pos),
        neut: hideNeutral ? 0 : Math.abs(exactPercentages.neut - rawPercentages.neut),
        neg: Math.abs(exactPercentages.neg - rawPercentages.neg)
    };
    
    // const totalPercentage = rawPercentages.pos + rawPercentages.neut + rawPercentages.neg;
    const totalPercentage = rawPercentages.pos + rawPercentages.neg;

    const diff = 100 - totalPercentage;
    
    // Find segment with largest rounding difference
    const segmentToAdjust = Object.entries(roundingDiffs)
        .filter(([key]) => key !== 'neut' || !hideNeutral)
        .sort(([, a], [, b]) => b - a)[0][0];
    
    return {
        display: rawPercentages,
        adjusted: {
            ...rawPercentages,
            [segmentToAdjust]: rawPercentages[segmentToAdjust] + diff
        }
    };
};


const formatSummaryText = (summary) => {
    if (!summary) return null;
    const [mainText, bulletPoints] = summary.split('Key Takeaways:');
    if (!bulletPoints) return summary;

    return (
        <>
            <p>{mainText.trim()}</p>
            <h4>Key Takeaways:</h4>
            <ul>
                {bulletPoints.split('•')
                    .map(point => point.trim())
                    .filter(Boolean)
                    .map((point, index) => (
                        <li key={index}>{point}</li>
                    ))
                }
            </ul>
        </>
    );
};

// Sub-components
const PreferenceBar = ({ percentages, topicName, competitor, onSegmentClick, hideNeutral }) => (
    <div 
        className="preference-bar"
        role="group"
        aria-label="Preference comparison"
    >
        <PreferenceSegment
            type="prefer-product"
            width={percentages.adjusted.pos}
            title={`${percentages.display.pos}% prefer ${topicName === "Market Overview" || competitor === "Market Overview" ? "competitors" : topicName}`}
            onClick={() => onSegmentClick('positive', competitor)}
        />
        {!hideNeutral && (
            <PreferenceSegment
                type="neutral"
                width={percentages.adjusted.neut}
                title={`${percentages.display.neut}% neutral`}
                onClick={() => onSegmentClick('neutral', competitor)}
            />
        )}
        <PreferenceSegment
            type="prefer-competitor"
            width={percentages.adjusted.neg}
            title={`${percentages.display.neg}% prefer ${competitor}`}
            onClick={() => onSegmentClick('negative', competitor)}
        />
    </div>
);

const PreferenceSegment = ({ type, width, title, onClick }) => (
    <div 
        className={`preference-segment ${type}`}
        style={{ width: `${width}%` }}
        title={title}
        onClick={(e) => {
            e.stopPropagation();
            onClick();
        }}
        role="button"
    />
);

const PreferenceLabels = ({ percentages, topicName, competitor, hideNeutral }) => (
    <div className="preference-labels">
        <PreferenceLabel
            percentage={percentages.display.pos}
            description={`prefer ${topicName}`}
        />
        {!hideNeutral && percentages.display.neut > 0 && (
            <PreferenceLabel
                percentage={percentages.display.neut}
                description="neutral"
            />
        )}
        <PreferenceLabel
            percentage={percentages.display.neg}
            description={competitor === "Market Overview" ? "prefer alternatives" : `prefer ${competitor}`}
        />
    </div>
);

const PreferenceLabel = ({ percentage, description }) => (
    <div className="preference-label">
        <span className="preference-percentage">{percentage}%</span>
        <span className="preference-description">{description}</span>
    </div>
);

/**
 * CompetitiveSummary Component
 * Displays competitive comparison information between two products/companies
 * 
 * @param {Object} props
 * @param {string} props.competitor - Name of the competitor
 * @param {Object} props.stats - Statistics object containing total, positive, negative, neutral counts
 * @param {boolean} props.expanded - Whether the detailed view is expanded
 * @param {Function} props.onToggle - Function to handle expansion toggle
 * @param {string} props.companyId - ID of the main company
 * @param {string} props.summary - Summary text of the comparison
 * @param {Object} props.competitorDetails - Detailed comparison information
 * @param {string} props.companyName - Name of the main company
 * @param {string} props.topicName - Name of the product/topic being compared
 * @param {Array} props.posts - Array of relevant posts
 * @param {Function} props.onSegmentClick - Handler for segment click events
 * @param {boolean} props.hideNeutral - Whether to hide the neutral segment (default: true)
 */
const CompetitiveSummary = ({ 
    competitor, 
    stats, 
    expanded, 
    onToggle, 
    companyId, 
    summary,
    competitorDetails,
    topicName,
    onSegmentClick,
    hideNeutral = true
}) => {
    const { total } = stats;
    const percentages = adjustPercentages(stats.positive, stats.neutral, stats.negative, total, hideNeutral);
    const formattedSummary = React.useMemo(() => formatSummaryText(summary), [summary]);

    return (
        <div className={`card-sm card competitor-card${expanded ? ' expanded' : ''}`}>
            <div className="competitor-header" onClick={onToggle}>
                <div className="competitor-title">
                    <div className="competitor-name">{competitor}</div>
                    <div className="competitor-actions">
                        <Link 
                            to={`/company/${companyId}?section=head-to-head`}
                            className="head-to-head-link"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <CompareArrowsIcon /> View Head-to-Head
                        </Link>
                    </div>
                </div>
                
                <div className="competitor-content">
                    <div className="preference-stats">
                        <PreferenceBar 
                            percentages={percentages}
                            topicName={topicName}
                            competitor={competitor}
                            onSegmentClick={onSegmentClick}
                            hideNeutral={hideNeutral}
                        />
                        <PreferenceLabels 
                            percentages={percentages}
                            topicName={topicName}
                            competitor={competitor}
                            hideNeutral={hideNeutral}
                        />
                    </div>
                    {summary && (
                        <div className="competitor-summary-text">
                            {formattedSummary}
                        </div>
                    )}
                </div>
                
                <div className="mention-count">
                    Based on {total} user {total === 1 ? 'mention' : 'mentions'}
                </div>
            </div>
            
            {expanded && (
                <div className="markdown-content">
                    <div className="markdown-header">
                        <h3>Detailed Comparison</h3>
                        <button 
                            className="close-button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggle();
                            }}
                            aria-label="Close details"
                        >
                            <CloseIcon />
                        </button>
                    </div>
                    <ReactMarkdown>{competitorDetails[competitor]?.text || ''}</ReactMarkdown>
                </div>
            )}
        </div>
    );
};

export default CompetitiveSummary; 